import type { Event, Member, ResultSet, Suggestion } from "../api/types";

const SITE_URL = "https://wheredowemeet.link";

function mapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

function coord(lat: number, lng: number): string {
  return `${lat.toFixed(6)},${lng.toFixed(6)}`;
}

/**
 * Google Maps link centred on the meeting zone showing all members and
 * venues. WhatsApp / iMessage / email clients render a rich map preview
 * from this URL automatically.
 */
export function meetingMapUrl(
  results: ResultSet,
): string {
  const c = results.zone.centroid;
  // Build a directions-style URL with waypoints so the preview shows
  // the full area. Falls back to a simple centre link if no venues.
  const top = results.results[0];
  if (top) {
    const dest = `${coord(top.venue.point.lat, top.venue.point.lng)}`;
    return (
      `https://www.google.com/maps/dir/?api=1` +
      `&destination=${dest}` +
      `&travelmode=driving`
    );
  }
  return (
    `https://www.google.com/maps/@${coord(c.lat, c.lng)},14z`
  );
}

function formatVenue(s: Suggestion, index: number): string {
  const { venue } = s;
  const lines: string[] = [];

  const rating = venue.rating != null
    ? ` \u00b7 ${venue.rating.toFixed(1)}\u2605`
    : "";
  const label = venue.subcategory
    ? `${venue.kind} \u00b7 ${venue.subcategory}`
    : venue.kind;
  lines.push(
    `${index + 1}. *${venue.name}* (${label}${rating})`
  );

  const perMember = s.per_member
    .map((m) => `${m.name} ${Math.round(m.travel_minutes)} min`)
    .join(" \u00b7 ");
  lines.push(`   ${perMember}`);

  if (venue.address) {
    lines.push(`   ${venue.address}`);
  }

  lines.push(
    `   ${mapsUrl(venue.point.lat, venue.point.lng)}`
  );

  return lines.join("\n");
}

function formatEvent(e: Event): string {
  const parts = [e.name];
  if (e.date) parts.push(e.date);
  if (e.venue_name) parts.push(`at ${e.venue_name}`);
  if (e.price) parts.push(e.price);
  if (e.ticket_url) parts.push(e.ticket_url);
  return `\u2022 ${parts.join(" \u2014 ")}`;
}

/** Build the full share text including a map link. */
export function formatShareText(
  results: ResultSet,
  members: Member[] = [],
): string {
  const top = results.results.slice(0, 5);
  const headline = results.zone.compromise_zone
    ? "Best compromise spots"
    : `Nobody drives more than ${Math.round(results.max_travel_minutes)} min`;

  let text = `*Where Do We Meet* \u2014 ${headline}\n`;
  text += `${SITE_URL}\n`;

  // Map link — WhatsApp/iMessage auto-render a rich map preview
  if (members.length > 0) {
    text += `\n${meetingMapUrl(results)}\n`;
  }

  // Member locations
  if (members.length > 0) {
    const labels = "ABCDEFGHIJ";
    const who = members
      .map((m, i) => `${labels[i] ?? i + 1}. ${m.name} \u2014 ${m.resolved_label}`)
      .join("\n");
    text += `\n${who}\n`;
  }

  text += "\n" + top.map((s, i) => formatVenue(s, i)).join("\n\n");

  const events = (results.events ?? []).slice(0, 3);
  if (events.length > 0) {
    text += "\n\n*Events nearby*\n";
    text += events.map(formatEvent).join("\n");
  }

  text += `\n\n\u2014\nFound with Where Do We Meet\n${SITE_URL}`;
  return text;
}

/** Plain text for email (no markdown bold). */
export function formatEmailText(
  results: ResultSet,
  members: Member[] = [],
): string {
  return formatShareText(results, members).replace(/\*/g, "");
}

/** Subject line for email. */
export function formatEmailSubject(results: ResultSet): string {
  const headline = results.zone.compromise_zone
    ? "Best compromise meeting spots"
    : `Meeting spots \u2014 nobody drives more than ${Math.round(results.max_travel_minutes)} min`;
  return `Where Do We Meet: ${headline}`;
}

/** WhatsApp deep link. */
export function whatsappUrl(
  results: ResultSet,
  members: Member[] = [],
): string {
  return `https://wa.me/?text=${encodeURIComponent(formatShareText(results, members))}`;
}

/** mailto: link. */
export function emailUrl(
  results: ResultSet,
  members: Member[] = [],
): string {
  const subject = encodeURIComponent(formatEmailSubject(results));
  const body = encodeURIComponent(formatEmailText(results, members));
  return `mailto:?subject=${subject}&body=${body}`;
}

/** Whether the browser supports the native Web Share API. */
export function canNativeShare(): boolean {
  return typeof navigator !== "undefined" && !!navigator.share;
}

/** Trigger native share sheet (mobile). */
export async function nativeShare(
  results: ResultSet,
  members: Member[] = [],
): Promise<void> {
  await navigator.share({
    title: formatEmailSubject(results),
    text: formatEmailText(results, members),
  });
}
